import type { ComponentType } from 'react';
import { ServiceDetailPage } from '@/components/ServiceDetailPage';

export type SameDayRemovalsPageConfig = {
  path: string;
  navLabel: string;
  cardTitle: string;
  title: string;
  description: string;
  intro: string;
  highlights: string[];
  process: string[];
  idealFor: string[];
};

function createSameDayRemovalsPage(config: SameDayRemovalsPageConfig): ComponentType {
  return function SameDayRemovalsPage() {
    return (
      <ServiceDetailPage
        activePath={config.path}
        serviceMenuPath="/services/same-day-moves"
        title={config.title}
        description={config.description}
        intro={config.intro}
        highlights={config.highlights}
        process={config.process}
        idealFor={config.idealFor}
      />
    );
  };
}

const baseHighlights = (location: string): string[] => [
  `Rapid response same day removals across ${location} and the surrounding area`,
  'Fully equipped vans, blankets, and straps ready to dispatch at short notice',
  'Experienced movers who can pack, load, and transport on the same day',
  'Clear, honest pricing even for last minute bookings',
];

const baseProcess = (location: string): string[] => [
  `Call us and share the basics of your ${location} move so we can confirm availability the same day.`,
  'Our team arrives promptly, assesses the job, and gets straight to work packing and loading.',
  'We transport your belongings safely and efficiently to your new address.',
  'We unload, position your items where you want them, and make sure you are happy before we leave.',
];

const baseIdealFor = (location: string): string[] => [
  `Tenants and homeowners in ${location} facing urgent or last minute moves`,
  'Landlords and letting agents who need a property cleared quickly',
  'Businesses relocating small offices, equipment, or stock at short notice',
];

export const sameDayRemovalsPageConfigs: SameDayRemovalsPageConfig[] = [
  {
    path: '/services/same-day-moves/staffordshire',
    navLabel: 'Staffordshire',
    cardTitle: 'Same Day Removals in Staffordshire',
    title: 'Same Day Removals in Staffordshire',
    description: 'Fast, professional same day removals across Staffordshire from VIP House Movers, ready to move you when time is tight.',
    intro: 'When plans change at the last minute, our Staffordshire same day removals service steps in with experienced movers, well equipped vans, and a calm, organised approach to get you moved quickly.',
    highlights: baseHighlights('Staffordshire'),
    process: baseProcess('Staffordshire'),
    idealFor: baseIdealFor('Staffordshire'),
  },
  {
    path: '/services/same-day-moves/wolverhampton',
    navLabel: 'Wolverhampton',
    cardTitle: 'Same Day Removals in Wolverhampton',
    title: 'Same Day Removals in Wolverhampton',
    description: 'VIP House Movers provides reliable same day removals in Wolverhampton for urgent home, flat, and office moves.',
    intro: 'Based in Wolverhampton, our team is perfectly placed to respond quickly when you need a same day move, handling everything from small flats to full houses with professionalism and care.',
    highlights: baseHighlights('Wolverhampton'),
    process: baseProcess('Wolverhampton'),
    idealFor: baseIdealFor('Wolverhampton'),
  },
  {
    path: '/services/same-day-moves/birmingham',
    navLabel: 'Birmingham',
    cardTitle: 'Same Day Removals in Birmingham',
    title: 'Same Day Removals in Birmingham',
    description: 'Reliable same day removals in Birmingham by VIP House Movers, designed for urgent home moves and short notice relocations.',
    intro: 'Our Birmingham same day removals service is built for those moments when moving cannot wait. We bring the manpower, vans, and experience needed to complete the job smoothly, even on the same day you call.',
    highlights: baseHighlights('Birmingham'),
    process: baseProcess('Birmingham'),
    idealFor: baseIdealFor('Birmingham'),
  },
  {
    path: '/services/same-day-moves/walsall',
    navLabel: 'Walsall',
    cardTitle: 'Same Day Removals in Walsall',
    title: 'Same Day Removals in Walsall',
    description: 'Fast, organised same day removals in Walsall from VIP House Movers, ready to respond when your move cannot wait.',
    intro: 'Whether you have been let down by another company or simply need to move at short notice, our Walsall same day removals team gets straight to work so your move still happens on time.',
    highlights: baseHighlights('Walsall'),
    process: baseProcess('Walsall'),
    idealFor: baseIdealFor('Walsall'),
  },
  {
    path: '/services/same-day-moves/cannock',
    navLabel: 'Cannock',
    cardTitle: 'Same Day Removals in Cannock',
    title: 'Same Day Removals in Cannock',
    description: 'Professional same day removals in Cannock by VIP House Movers, combining speed with the same care we put into every move.',
    intro: 'When you need to move in a hurry around Cannock, our team can often arrange a same day service, taking the pressure off while handling your belongings with full care and attention.',
    highlights: baseHighlights('Cannock'),
    process: baseProcess('Cannock'),
    idealFor: baseIdealFor('Cannock'),
  },
];

export const sameDayRemovalsPages = sameDayRemovalsPageConfigs.map((config) => ({
  ...config,
  component: createSameDayRemovalsPage(config),
}));
